// Draw in eye-relative coordinates: one unit is the distance between eye centers.
// This keeps accessories proportional and follows head roll without blink-based sizing.
export function eyePose(landmarks) {
  const center = points => {
    if (!points?.length || points.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
      throw new Error('Eye landmarks unavailable');
    }
    return points.reduce((sum, p) => ({ x: sum.x + p.x / points.length, y: sum.y + p.y / points.length }), { x: 0, y: 0 });
  };
  const eyes = [center(landmarks.getLeftEye()), center(landmarks.getRightEye())].sort((a, b) => a.x - b.x);
  const [left, right] = eyes;
  const distance = Math.hypot(right.x - left.x, right.y - left.y);
  if (distance < 2) throw new Error('Eye landmarks too close');
  return { x: (left.x + right.x) / 2, y: (left.y + right.y) / 2,
    angle: Math.atan2(right.y - left.y, right.x - left.x), distance };
}

function onFace(ctx, landmarks, draw) {
  const pose = eyePose(landmarks);
  ctx.save();
  try {
    ctx.translate(pose.x, pose.y);
    ctx.rotate(pose.angle);
    ctx.scale(pose.distance, pose.distance);
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    draw();
  } finally { ctx.restore(); }
}

function lens(ctx, x) {
  ctx.beginPath();
  ctx.moveTo(x - .37, -.21);
  ctx.bezierCurveTo(x - .42, -.05, x - .35, .27, x - .12, .29);
  ctx.bezierCurveTo(x + .18, .34, x + .35, .16, x + .37, -.17);
  ctx.bezierCurveTo(x + .16, -.27, x - .18, -.29, x - .37, -.21);
  ctx.closePath();
}

function eyewear(ctx, landmarks, warm) {
  onFace(ctx, landmarks, () => {
    // Short temple arms sit behind the frames.
    ctx.strokeStyle = warm ? '#674735' : '#a9afb7';
    ctx.lineWidth = .038;
    ctx.beginPath();
    ctx.moveTo(-.86, -.13); ctx.lineTo(-1.02, -.19);
    ctx.moveTo(.86, -.13); ctx.lineTo(1.02, -.19); ctx.stroke();
    const tint = ctx.createLinearGradient(0, -.26, 0, .32);
    tint.addColorStop(0, warm ? 'rgba(53,32,22,.94)' : 'rgba(17,25,37,.95)');
    tint.addColorStop(1, warm ? 'rgba(140,99,62,.62)' : 'rgba(72,91,112,.62)');
    const rim = ctx.createLinearGradient(-1, -.3, 1, .3);
    rim.addColorStop(0, warm ? '#382317' : '#757e89');
    rim.addColorStop(.35, warm ? '#bc8c51' : '#ece7da');
    rim.addColorStop(.6, warm ? '#54321f' : '#8993a1');
    rim.addColorStop(1, warm ? '#a87745' : '#d7d9de');
    [-.5, .5].forEach(x => {
      lens(ctx, x);
      ctx.fillStyle = tint; ctx.fill();
      ctx.strokeStyle = rim; ctx.lineWidth = warm ? .06 : .024; ctx.stroke();
      ctx.save();
      lens(ctx, x); ctx.clip();
      const reflection = ctx.createLinearGradient(x - .2, -.25, x + .3, .3);
      reflection.addColorStop(0, 'rgba(255,255,255,.24)');
      reflection.addColorStop(.48, 'rgba(255,255,255,.02)');
      reflection.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = reflection;
      ctx.beginPath(); ctx.moveTo(x - .32, -.3); ctx.lineTo(x - .05, -.3);
      ctx.lineTo(x + .32, .32); ctx.lineTo(x + .16, .32); ctx.closePath(); ctx.fill();
      ctx.restore();
      ctx.fillStyle = '#dfcaa2';
      ctx.beginPath(); ctx.ellipse(x + (x < 0 ? -.31 : .31), -.16, .022, .009, 0, 0, Math.PI * 2); ctx.fill();
    });
    ctx.strokeStyle = rim; ctx.lineWidth = warm ? .046 : .027;
    ctx.beginPath(); ctx.moveTo(-.14, -.1); ctx.quadraticCurveTo(0, -.19, .14, -.1); ctx.stroke();
  });
}

function masquerade(ctx, landmarks, pearl) {
  onFace(ctx, landmarks, () => {
    const satin = ctx.createLinearGradient(-1, -.45, 1, .45);
    satin.addColorStop(0, pearl ? '#a69caa' : '#171e2e');
    satin.addColorStop(.4, pearl ? '#eee6e1' : '#48566b');
    satin.addColorStop(.65, pearl ? '#c9bbc8' : '#242c40');
    satin.addColorStop(1, pearl ? '#f2e7db' : '#111626');
    ctx.beginPath();
    ctx.moveTo(-1.04, -.4);
    ctx.bezierCurveTo(-.6, -.54, -.27, -.38, 0, -.25);
    ctx.bezierCurveTo(.27, -.38, .6, -.54, 1.04, -.4);
    ctx.bezierCurveTo(.94, .17, .6, .47, .23, .32);
    ctx.lineTo(0, .49); ctx.lineTo(-.23, .32);
    ctx.bezierCurveTo(-.6, .47, -.94, .17, -1.04, -.4);
    ctx.closePath();
    // Eye openings are cut out of the path, leaving actual eyes visible.
    [-.5, .5].forEach(x => {
      ctx.moveTo(x + .29, .01);
      ctx.ellipse(x, .01, .29, .14, 0, 0, Math.PI * 2);
    });
    ctx.fillStyle = satin; ctx.fill('evenodd');
    ctx.strokeStyle = pearl ? 'rgba(255,247,226,.8)' : 'rgba(205,185,143,.85)';
    ctx.lineWidth = .015; ctx.stroke();
    // Restrained etched edge detail.
    ctx.lineWidth = .009;
    [-1, 1].forEach(side => {
      ctx.beginPath(); ctx.moveTo(side * .25, -.31);
      ctx.quadraticCurveTo(side * .6, -.47, side * .91, -.36); ctx.stroke();
    });
  });
}

function constellation(ctx, landmarks) {
  onFace(ctx, landmarks, () => {
    [-1, 1].forEach(side => {
      const dots = [[.52, .39], [.7, .44], [.87, .33], [.98, .48]];
      ctx.strokeStyle = 'rgba(225,206,167,.5)'; ctx.lineWidth = .009;
      ctx.beginPath(); dots.forEach(([x, y], i) => i ? ctx.lineTo(side * x, y) : ctx.moveTo(side * x, y)); ctx.stroke();
      dots.forEach(([x, y], index) => {
        ctx.fillStyle = index === 1 ? '#fff4d6' : '#d8be88';
        ctx.beginPath(); ctx.arc(side * x, y, index === 1 ? .024 : .015, 0, Math.PI * 2); ctx.fill();
      });
    });
  });
}

export const STUDIO_FILTERS = {
  smoke: { id: 'smoke', name: "עשן", icon: '◒', description: "עדשות כהות מדורגות · מסגרת כסף מט", apply: (ctx, frame, landmarks) => eyewear(ctx, landmarks, false) },
  amber: { id: 'amber', name: "ענבר", icon: '◒', description: "עדשות בגוון חם · מסגרת בהשראת שריון צב", apply: (ctx, frame, landmarks) => eyewear(ctx, landmarks, true) },
  midnight: { id: 'midnight', name: "חצות", icon: '◇', description: "מסכת סאטן כחולה · שוליים עדינים בצבע שמפניה", apply: (ctx, frame, landmarks) => masquerade(ctx, landmarks, false) },
  pearl: { id: 'pearl', name: "פנינה", icon: '◇', description: "מסכת פנינה · עיניים פתוחות ושוליים מעוצבים", apply: (ctx, frame, landmarks) => masquerade(ctx, landmarks, true) },
  constellation: { id: 'constellation', name: "קבוצת כוכבים", icon: '✧', description: "עיטורי זהב עדינים לאורך עצמות הלחיים", apply: (ctx, frame, landmarks) => constellation(ctx, landmarks) },
};
